import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Contrat du rig partagé : interdit. La pièce n'est éclairée que par ses
// softboxes et sa rampe LED, et elles n'existent que dans les reflets.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'ferro',
  family: 'object',
  danceability: 0.80,
  energyLevel: 0.78,
  bpmMin: 100,
  bpmMax: 160,
  movement: 0.65,
  tonality: 0.40,
  prefers: { lowEnd: [0.55, 1.0], percussiveness: [0.45, 1.0] },
  enabled: true
};

// Ferro : du ferrofluide noir dans une coupelle, dont la surface se hérisse en
// pointes sur chaque kick et se relâche entre eux. Tout est noir ; l'image
// n'existe que par les reflets.
//
// L'INSTABILITÉ DE ROSENSWEIG. Au-delà d'un champ magnétique seuil, la surface
// plane d'un fluide magnétique se brise en un réseau de pointes. Ici un aimant
// sous la coupelle, piloté par les graves.
//
// LE RÉSEAU HEXAGONAL, le détail qui fait reconnaître l'image : les pointes ne
// sont pas réparties au hasard, elles s'empilent toutes à égale distance de
// leurs six voisines. La somme de trois cosinus à 120 degrés produit ce
// réseau EXACTEMENT, et il est fixe dans le repère de la coupelle : l'aimant
// qui se déplace ne fait glisser que l'enveloppe, les pointes disparaissent
// d'un côté et surgissent de l'autre, et le réseau reste aligné.
//
//   h(p) = H × enveloppe(p) × pointes(p) + ménisque(|p|)
//   pointes : s = (cos(k e1·p) + cos(k e2·p) + cos(k e3·p) + 1.5) / 4.5
//             pow(s, 3) pour des pointes effilées et des creux larges, plus un
//             affûtage en racine carrée tout près du sommet, dont la pente est
//             infinie au sommet : la pointe est presque un point.
//   enveloppe : une gaussienne centrée sur l'aimant, avec un plancher, pour
//             qu'au repos une houle de pointes basses persiste.
//
// LES NORMALES. La fiche prévoit des différences finies dans le vertex shader.
// Elles y sont inutiles : le champ est analytique, son gradient aussi, et il
// est évalué PAR PIXEL dans le fragment shader (trois sinus, une exponentielle).
// Un reflet est une ligne fine sur le flanc d'un cône ; interpolé entre des
// sommets espacés de 0,07 au bord de la coupelle, il devenait une tache.
// Par pixel, il reste une ligne, à tous les tiers, et la grille ne sert plus
// qu'à la silhouette.
//
// LE RENDU. Le fluide est noir (0,02) ; tout vient d'une cube map procédurale
// de 6 × 64 px qui ne contient PAS de couleur mais quatre MASQUES : R, G, B
// pour les trois softboxes, A pour la rampe LED. Les couleurs sont des
// uniformes : la palette change, la texture non, et la rampe peut passer au
// blanc sur un drop sans rien reconstruire. Fresnel sur les flancs rasants.
//
// La scène est rendue dans une cible MSAA 4× privée (le motif de metronome.js,
// voir [[no-antialiasing-private-msaa-target]]) : les reflets sont des lignes
// d'un pixel sur un solide, et la chaîne partagée n'a aucun anti-crénelage.

// --- la coupelle ---------------------------------------------------------------
const R_DISH = 3.0;                  // 6 unités de diamètre
const MENISCUS_H = 0.05;             // le fluide touche le rebord
const MENISCUS_W = 0.35;
const RIM_R = 0.09;                  // le rebord : un tore
const WALL_H = 0.75;
const TABLE_Y = -0.72;
const MAGNET_R = 1.0, MAGNET_H = 0.45;

// --- le réseau -------------------------------------------------------------------
// Le pas du réseau (distance entre deux pointes) : plus serré sur les médiums.
// k = 4π / (√3 × pas) pour la somme de trois cosinus.
const PITCH_LOW = 0.56;
const PITCH_HIGH = 0.34;
const TIP_W = 0.22;                  // largeur (en s) de l'affûtage du sommet
const TIP_A = 0.35;                  // sa hauteur, relative à pow(s,3)

// --- le champ ----------------------------------------------------------------------
const H_REST = 0.07;                 // la houle de repos : jamais plat
const H_PEAK_MIN = 0.32;             // le jaillissement d'un kick, selon bands.low
const H_PEAK_MAX = 0.80;
const H_DROP = 0.95;
const ATTACK_S = 0.03;               // le champ monte en 30 ms
const DECAY_S = 0.085;               // et redescend en 250 ms (3 τ)
const ENV_FLOOR = 0.22;              // l'enveloppe ne tombe jamais à zéro
const SIGMA_MIN = 0.95;              // rayon de l'enveloppe au début de la mesure
const SIGMA_MAX = 2.7;               // ... et à sa fin : jusqu'au bord
const SIGMA_TAU_S = 0.12;            // le retour en début de mesure est adouci
const MAGNET_RMIN = 0.9;             // l'aimant n'est jamais sous la pochette
const MAGNET_RMAX = 1.5;
const MOVE_BEATS = 2;                // le déplacement de l'aimant, en temps
const DROP_SCORE_MIN = 0.7;
const DROP_STALE_MS = 1500;

// --- le studio ------------------------------------------------------------------------
// Trois softboxes en arc autour de la coupelle : des bandes VERTICALES,
// étroites et hautes, basses sur l'horizon. Une pointe est un cône : sa pente
// varie le long du flanc, l'azimut de sa normale tout autour. Une bande
// verticale (petit azimut, grande hauteur) se reflète donc en une LIGNE le
// long du flanc, de la base au sommet ; une bande horizontale haute ne se
// refléterait qu'en un anneau à la hauteur où la pente lui correspond, et
// surtout en larges nappes de couleur sur les pentes douces entre les pointes.
// Trois bandes réparties sur 220 degrés : quelle que soit la position de la
// caméra sur son orbite, chaque pointe a une bande à renvoyer.
const SOFTBOXES = [
  { az: -110, el: 30, hw: 2.6, hh: 32 },
  { az: 0, el: 30, hw: 2.6, hh: 32 },
  { az: 110, el: 30, hw: 2.6, hh: 32 }
];
const SOFTBOX_SOFT = 1.2;            // degrés de flou au bord
// La surface plane renvoie exactement l'élévation de la caméra (35°) : sans
// rien, la bande du fond se reflète en une large nappe au milieu du fluide.
// Un creux dans chaque bande autour de cette élévation la retire des zones
// planes ; les pentes, même douces, la retrouvent de part et d'autre, en
// lignes fines - la houle de repos se lit en contours, pas en nappe.
const NOTCH_EL = 35;                 // = PLUNGE_DEG
const NOTCH_HW = 7;
const NOTCH_DEPTH = 0.75;
const RING_POLAR = 36;               // la rampe LED : un cercle autour du zénith
const RING_WIDTH = 1.5;
const RING_SOFT = 0.9;
const ENV_SIZE = 64;
const BOX_RADIANCE = 6.5;            // la palette, saturée à 0,75, à cette radiance
const BOX_SATURATION = 0.75;
const BOX_LIGHTNESS = 0.60;
const RING_RADIANCE_MIN = 0.9;
const RING_RADIANCE_MAX = 3.2;       // selon bands.high
const RING_DROP = 9.0;               // blanc pur
const F0 = 0.10;                     // le fluide : un diélectrique brillant
const BASE_ALBEDO = 0.02;

// --- la caméra ------------------------------------------------------------------------
const PLUNGE_DEG = 35;               // = NOTCH_EL
const DISH_SHARE = 0.70;             // la coupelle occupe 70 % de la largeur
const DISH_SHARE_H = 0.88;           // ... et au plus 88 % de la hauteur (les écrans 3,2:1 du Pi)
const ORBIT_S = 120;
const LOOK_Y = 0.05;

// Mesuré sur le Pi (1280 x 400, chaîne complète, voir le banc dans la note de
// mémoire) : au tier 1, le MSAA 4x coûte 4,5 ms sur un plancher de composer
// d'environ 11 ms ; les normales aux sommets (?ferrovn=1) ne rendent que 1 à
// 3 ms et font des reflets à facettes - gardées comme réglage, pas comme
// tier. Le tier 0 rend DIRECTEMENT dans la scène partagée (samples 0 : ni
// cible privée, ni quad), le tier 1 garde un MSAA 2x pour que les lignes de
// reflet ne rampent pas.
const TIER = {
  0: { seg: 96, rings: 48, softboxes: 1, meniscus: false, samples: 0, pixelNormals: true },
  1: { seg: 160, rings: 80, softboxes: 3, meniscus: false, samples: 2, pixelNormals: true },
  2: { seg: 256, rings: 128, softboxes: 3, meniscus: true, samples: 4, pixelNormals: true }
};

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function smoothstep(a, b, x) { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
export function kForPitch(pitch) { return 4 * Math.PI / (Math.sqrt(3) * pitch); }

// =============================================================================
// LE CHAMP DE HAUTEUR - un seul chunk, partagé par les deux étages.
// =============================================================================
const FIELD_CHUNK = `
  uniform float uH;
  uniform float uK;
  uniform vec2 uMagnet;
  uniform float uSigma;
  uniform float uMeniscus;
  const float R_DISH = ${R_DISH.toFixed(3)};
  const float ENV_FLOOR = ${ENV_FLOOR.toFixed(3)};
  const float TIP_W = ${TIP_W.toFixed(3)};
  const float TIP_A = ${TIP_A.toFixed(3)};
  const float EDGE0 = ${(R_DISH - 0.50).toFixed(3)};
  const float EDGE1 = ${(R_DISH - 0.08).toFixed(3)};
  const float MEN_W = ${MENISCUS_W.toFixed(3)};
  const vec2 E1 = vec2(1.0, 0.0);
  const vec2 E2 = vec2(-0.5, 0.8660254);
  const vec2 E3 = vec2(-0.5, -0.8660254);

  // s dans [0,1] : 1 aux noeuds du réseau hexagonal, 0 au centre des creux.
  float lattice(vec2 p) {
    return (cos(uK * dot(E1, p)) + cos(uK * dot(E2, p)) + cos(uK * dot(E3, p)) + 1.5) / 4.5;
  }
  vec2 latticeGrad(vec2 p) {
    return (-uK / 4.5) * (E1 * sin(uK * dot(E1, p)) + E2 * sin(uK * dot(E2, p)) + E3 * sin(uK * dot(E3, p)));
  }
  // L'affûtage : pow(s,3) + une racine près du sommet, normalisé à 1.
  float spike(float s) {
    float t = clamp((1.0 - s) / TIP_W, 0.0, 1.0);
    return (s * s * s + TIP_A * (1.0 - sqrt(t))) / (1.0 + TIP_A);
  }
  float spikeD(float s) {
    float t = (1.0 - s) / TIP_W;
    float d = 3.0 * s * s;
    if (t < 1.0) d += TIP_A * 0.5 / (TIP_W * sqrt(max(t, 0.002)));
    return d / (1.0 + TIP_A);
  }
  // Hauteur et gradient (dh/dx, dh/dz) en un point de la coupelle.
  vec3 field(vec2 p) {
    float r = length(p);
    vec2 rdir = r > 1e-4 ? p / r : vec2(1.0, 0.0);
    // l'enveloppe : gaussienne sur l'aimant, avec son plancher
    vec2 q = p - uMagnet;
    float g = exp(-dot(q, q) / (uSigma * uSigma));
    float env = ENV_FLOOR + (1.0 - ENV_FLOOR) * g;
    vec2 envG = (1.0 - ENV_FLOOR) * g * (-2.0 * q / (uSigma * uSigma));
    // le bord : les pointes rétrécissent jusqu'à une surface plane
    float x = clamp((r - EDGE0) / (EDGE1 - EDGE0), 0.0, 1.0);
    float edge = 1.0 - x * x * (3.0 - 2.0 * x);
    float edgeD = -6.0 * x * (1.0 - x) / (EDGE1 - EDGE0);
    // le réseau
    float s = lattice(p);
    float sp = spike(s);
    vec2 spG = spikeD(s) * latticeGrad(p);
    // le ménisque : le fluide remonte sur le rebord
    float m = clamp((r - (R_DISH - MEN_W)) / MEN_W, 0.0, 1.0);
    float men = uMeniscus * m * m;
    float menD = uMeniscus * 2.0 * m / MEN_W;
    float h = uH * env * edge * sp + men;
    vec2 gh = uH * (envG * edge * sp + env * edgeD * sp * rdir + env * edge * spG) + menD * rdir;
    return vec3(h, gh);
  }
`;

// Le chunk de zone de sécurité, avec un plancher de 0,5 (le chunk partagé
// descend à 0,32 ; ici la coupelle EST le sujet, elle ne doit pas s'éteindre).
const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.5, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const fluidVertexShader = `
  ${FIELD_CHUNK}
  ${SAFE_CHUNK}
  uniform float uFlat;
  varying vec3 vWorld;
  varying vec2 vP;
  varying float vAtt;
  varying vec3 vGrad;
  void main() {
    vec2 p = position.xz;
    vec3 f = field(p);
    vGrad = f;
    vec3 pos = vec3(p.x, f.x * (1.0 - uFlat), p.y);
    vec4 world = modelMatrix * vec4(pos, 1.0);
    vWorld = world.xyz;
    vP = p;
    vec4 clip = projectionMatrix * viewMatrix * world;
    vAtt = uFlat > 0.5 ? 1.0 : safeAtt(clip);
    gl_Position = clip;
  }
`;

// L'environnement : quatre masques dans la cube map, les couleurs en uniformes.
const ENV_CHUNK = `
  uniform samplerCube uEnv;
  uniform vec3 uBox0;
  uniform vec3 uBox1;
  uniform vec3 uBox2;
  uniform vec3 uRing;
  vec3 envRadiance(vec3 dir, float lod) {
    vec4 m = textureCube(uEnv, dir, lod);
    return m.r * uBox0 + m.g * uBox1 + m.b * uBox2 + m.a * uRing;
  }
`;

const fluidFragmentShader = `
  ${FIELD_CHUNK}
  ${ENV_CHUNK}
  uniform float uFlat;
  uniform float uF0;
  varying vec3 vWorld;
  varying vec2 vP;
  varying float vAtt;
  varying vec3 vGrad;
  void main() {
    // VERTEX_NORMALS (tiers du Pi) : le gradient interpolé entre les sommets ;
    // sinon le champ est réévalué par pixel.
    #ifdef VERTEX_NORMALS
    vec3 f = vGrad;
    #else
    vec3 f = field(vP);
    #endif
    if (uFlat > 0.5) {
      // ?ferroflat : le champ de hauteur en niveaux de gris (0,8 au sommet,
      // pour que les sondes trouvent un maximum et pas un plateau saturé)
      float g = clamp(0.8 * f.x / max(uH, 0.01), 0.0, 1.0);
      gl_FragColor = vec4(vec3(g), 1.0);
      return;
    }
    vec3 n = normalize(vec3(-f.y, 1.0, -f.z));
    vec3 v = normalize(cameraPosition - vWorld);
    float ndv = max(dot(n, v), 0.0);
    vec3 r = reflect(-v, n);
    // Fresnel : les flancs rasants reflètent davantage
    float fr = uF0 + (1.0 - uF0) * pow(1.0 - ndv, 5.0);
    vec3 refl = envRadiance(r, 0.0) * fr;
    vec3 col = vec3(${BASE_ALBEDO.toFixed(3)}) * (0.4 + 0.6 * n.y) + refl;
    gl_FragColor = vec4(col * vAtt, 1.0);
  }
`;

// Les solides : rebord, paroi, aimant, table - des normales de géométrie.
const solidVertexShader = `
  ${SAFE_CHUNK}
  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vAtt;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    vNormal = normalize(mat3(modelMatrix) * normal);
    vec4 clip = projectionMatrix * viewMatrix * world;
    vAtt = safeAtt(clip);
    gl_Position = clip;
  }
`;

// La table : noire, mate, une flaque de la rampe LED autour de la coupelle.
// Pas de cube map : c'est un plein écran, chaque instruction y compte sur le Pi.
const tableFragmentShader = `
  uniform vec3 uRing;
  uniform vec3 uBase;
  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vAtt;
  void main() {
    float d2 = dot(vWorld.xz, vWorld.xz);
    vec3 col = uBase + uRing * 0.012 * exp(-d2 / 28.0);
    gl_FragColor = vec4(col * vAtt, 1.0);
  }
`;

const solidFragmentShader = `
  ${ENV_CHUNK}
  uniform vec3 uBase;
  uniform float uGain;
  uniform float uRough;
  uniform float uF0;
  uniform float uOpacity;
  uniform float uPool;
  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vAtt;
  void main() {
    vec3 n = normalize(vNormal);
    vec3 v = normalize(cameraPosition - vWorld);
    float ndv = max(dot(n, v), 0.0);
    vec3 r = reflect(-v, n);
    float fr = uF0 + (1.0 - uF0) * pow(1.0 - ndv, 5.0);
    vec3 col = uBase + envRadiance(r, uRough) * fr * uGain;
    // la flaque de la rampe LED sur la table, autour de la coupelle
    float d2 = dot(vWorld.xz, vWorld.xz);
    col += uPool * uRing * 0.012 * exp(-d2 / 28.0);
    gl_FragColor = vec4(col * vAtt, uOpacity);
  }
`;

// =============================================================================
// L'ENVIRONNEMENT - une cube map de 6 × 64 px, quatre masques.
// =============================================================================

// La direction d'un texel : la table du cube map GL, ligne 0 en haut.
function cubeDirection(face, s, t) {
  switch (face) {
    case 0: return [1, -t, -s];
    case 1: return [-1, -t, s];
    case 2: return [s, 1, t];
    case 3: return [s, -1, -t];
    case 4: return [s, -t, 1];
    default: return [-s, -t, -1];
  }
}

function softboxMask(d, box) {
  const az = THREE.MathUtils.degToRad(box.az), el = THREE.MathUtils.degToRad(box.el);
  // le centre de la softbox et son repère (droite, haut)
  const c = [Math.cos(el) * Math.sin(az), Math.sin(el), -Math.cos(el) * Math.cos(az)];
  const right = [Math.cos(az), 0, Math.sin(az)];
  const up = [-Math.sin(el) * Math.sin(az), Math.cos(el), Math.sin(el) * Math.cos(az)];
  const w = d[0] * c[0] + d[1] * c[1] + d[2] * c[2];
  if (w <= 0.02) return 0;
  const u = (d[0] * right[0] + d[1] * right[1] + d[2] * right[2]) / w;
  const vv = (d[0] * up[0] + d[1] * up[1] + d[2] * up[2]) / w;
  const au = Math.atan(Math.abs(u)) * 180 / Math.PI;
  const av = Math.atan(Math.abs(vv)) * 180 / Math.PI;
  const mu = 1 - smoothstep(box.hw - SOFTBOX_SOFT, box.hw + SOFTBOX_SOFT, au);
  const mv = 1 - smoothstep(box.hh - SOFTBOX_SOFT, box.hh + SOFTBOX_SOFT, av);
  const elev = Math.asin(Math.max(-1, Math.min(1, d[1]))) * 180 / Math.PI;
  const notch = 1 - NOTCH_DEPTH * (1 - smoothstep(NOTCH_HW * 0.5, NOTCH_HW, Math.abs(elev - NOTCH_EL)));
  return mu * mv * notch;
}

function ringMask(d) {
  const polar = Math.acos(Math.max(-1, Math.min(1, d[1]))) * 180 / Math.PI;
  const a = Math.abs(polar - RING_POLAR);
  return 1 - smoothstep(RING_WIDTH - RING_SOFT, RING_WIDTH + RING_SOFT, a);
}

export function buildEnvironment(softboxCount) {
  const n = ENV_SIZE;
  const faces = [];
  for (let f = 0; f < 6; f++) {
    const data = new Uint8Array(n * n * 4);
    for (let j = 0; j < n; j++) {
      const t = ((j + 0.5) / n) * 2 - 1;
      for (let i = 0; i < n; i++) {
        const s = ((i + 0.5) / n) * 2 - 1;
        const raw = cubeDirection(f, s, t);
        const l = Math.hypot(raw[0], raw[1], raw[2]);
        const d = [raw[0] / l, raw[1] / l, raw[2] / l];
        const o = (j * n + i) * 4;
        for (let b = 0; b < 3; b++) {
          data[o + b] = b < softboxCount ? Math.round(255 * softboxMask(d, SOFTBOXES[b])) : 0;
        }
        data[o + 3] = Math.round(255 * ringMask(d));
      }
    }
    const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
    tex.needsUpdate = true;
    faces.push(tex);
  }
  const cube = new THREE.CubeTexture(faces);
  cube.format = THREE.RGBAFormat;
  cube.type = THREE.UnsignedByteType;
  cube.colorSpace = THREE.NoColorSpace;
  cube.generateMipmaps = true;
  cube.minFilter = THREE.LinearMipmapLinearFilter;
  cube.magFilter = THREE.LinearFilter;
  cube.needsUpdate = true;
  return cube;
}

// =============================================================================
// LA GRILLE POLAIRE
// =============================================================================
function buildDishGeometry(seg, rings) {
  const pos = new Float32Array(seg * rings * 3);
  for (let i = 0; i < rings; i++) {
    const r = R_DISH * i / (rings - 1);
    for (let j = 0; j < seg; j++) {
      const th = 2 * Math.PI * j / seg;
      const o = (i * seg + j) * 3;
      pos[o] = r * Math.cos(th); pos[o + 1] = 0; pos[o + 2] = r * Math.sin(th);
    }
  }
  const idx = [];
  for (let i = 0; i < rings - 1; i++) {
    for (let j = 0; j < seg; j++) {
      const j1 = (j + 1) % seg;
      const a = i * seg + j, b = i * seg + j1, c = (i + 1) * seg + j, d = (i + 1) * seg + j1;
      // face avant vue du dessus (+y) : x vers la droite, z vers le bas de
      // l'écran, donc theta croissant tourne dans le sens horaire - l'ordre
      // antihoraire est a, d, c.
      idx.push(a, d, c);
      if (i > 0) idx.push(a, b, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0.3, 0), R_DISH + 1.2);
  return g;
}

const _size = new THREE.Vector2();
const _color = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

export class FerroVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const fh = parseFloat(urlParam('ferroh'));
    this._forceH = Number.isFinite(fh) ? fh : null;
    const fk = parseFloat(urlParam('ferrok'));
    this._forceK = Number.isFinite(fk) ? fk : null;
    this._flat = urlParam('ferroflat') === '1';
    const fs = parseFloat(urlParam('ferrosigma'));
    this._forceSigma = Number.isFinite(fs) ? fs : null;   // rayon de l'enveloppe, pour les sondes
    this._forceDrop = urlParam('ferrodrop') === '1';
    // réglage sur le Pi : ?ferroaa=<n> échantillons MSAA, ?ferrovn=1|0 normales aux sommets
    const aa = parseInt(urlParam('ferroaa'), 10);
    this._samples = Number.isFinite(aa) ? aa : this._layout.samples;
    const vn = urlParam('ferrovn');
    this._pixelNormals = vn != null ? vn !== '1' : this._layout.pixelNormals;
    // Les reflets sont déjà des lignes de lumière : moins de bloom qu'ailleurs,
    // pour qu'ils restent NETS.
    this._postProcessing = { bloomStrength: 0.45, bloomThreshold: 0.80 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    const self = this;
    // samples 0 : tout va directement dans la scène partagée
    this._direct = this._samples === 0;
    this._inner = this._direct ? scene : new THREE.Scene();
    this._meshes = [];

    this._env = buildEnvironment(L.softboxes);
    this._kick = { age: 10, amp: 0 };
    this._time = 0;
    this._H = H_REST;
    this._sigma = SIGMA_MIN;
    this._k = null;                 // posé sur la cible au premier update : pas de glissement d'entrée
    this._kTarget = kForPitch(PITCH_LOW);
    this._magnet = new THREE.Vector2(MAGNET_RMIN, 0);
    this._magnetFrom = this._magnet.clone();
    this._magnetTo = this._magnet.clone();
    this._moveT = 1;
    this._moveS = 1;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._free = 0; this._beats = 0;
    this._secondsBeat = 0.5;
    this._dropUntil = -1;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined;
    this._seed = 1234;

    this._sharedUniforms = {
      uEnv: { value: this._env },
      uBox0: { value: new THREE.Color(0.9, 0.5, 0.2).multiplyScalar(BOX_RADIANCE) },
      uBox1: { value: new THREE.Color(0.3, 0.6, 0.95).multiplyScalar(BOX_RADIANCE) },
      uBox2: { value: new THREE.Color(0.85, 0.3, 0.6).multiplyScalar(BOX_RADIANCE) },
      uRing: { value: new THREE.Color(1, 1, 1).multiplyScalar(RING_RADIANCE_MIN) },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };

    // --- le fluide ---
    this._uni = Object.assign({
      uH: { value: H_REST },
      uK: { value: this._kTarget },
      uMagnet: { value: this._magnet },
      uSigma: { value: SIGMA_MIN },
      uMeniscus: { value: L.meniscus ? MENISCUS_H : 0 },
      uFlat: { value: this._flat ? 1 : 0 },
      uF0: { value: F0 }
    }, this._sharedUniforms);
    this._fluidGeo = buildDishGeometry(L.seg, L.rings);
    this._fluidMat = new THREE.ShaderMaterial({
      uniforms: this._uni, vertexShader: fluidVertexShader, fragmentShader: fluidFragmentShader,
      defines: this._pixelNormals ? {} : { VERTEX_NORMALS: 1 }
    });
    this._fluid = new THREE.Mesh(this._fluidGeo, this._fluidMat);
    this._fluid.frustumCulled = false;
    this._inner.add(this._fluid); this._meshes.push(this._fluid);

    // --- la coupelle : rebord, paroi, et l'aimant qu'on devine à travers ---
    const solid = (base, gain, rough, f0, opacity, pool) => new THREE.ShaderMaterial({
      uniforms: Object.assign({
        uBase: { value: new THREE.Color(base, base, base) },
        uGain: { value: gain }, uRough: { value: rough }, uF0: { value: f0 },
        uOpacity: { value: opacity }, uPool: { value: pool }
      }, this._sharedUniforms),
      vertexShader: solidVertexShader, fragmentShader: solidFragmentShader,
      transparent: opacity < 1, depthWrite: opacity >= 1
    });
    this._rimGeo = new THREE.TorusGeometry(R_DISH + 0.04, RIM_R, 12, 128);
    this._rimGeo.rotateX(Math.PI / 2);
    this._rimMat = solid(0.015, 0.75, 2.0, 0.16, 1, 0);
    this._rim = new THREE.Mesh(this._rimGeo, this._rimMat);
    this._rim.position.y = MENISCUS_H;
    this._inner.add(this._rim); this._meshes.push(this._rim);

    this._wallGeo = new THREE.CylinderGeometry(R_DISH + 0.11, R_DISH + 0.02, WALL_H, 128, 1, true);
    this._wallMat = solid(0.012, 0.55, 2.5, 0.08, 0.86, 0);
    this._wall = new THREE.Mesh(this._wallGeo, this._wallMat);
    this._wall.position.y = MENISCUS_H - WALL_H / 2;
    this._wall.renderOrder = 2;
    this._inner.add(this._wall); this._meshes.push(this._wall);

    this._magnetGeo = new THREE.CylinderGeometry(MAGNET_R, MAGNET_R, MAGNET_H, 48);
    this._magnetMat = solid(0.05, 0.22, 3.0, 0.10, 1, 0);
    this._magnetMesh = new THREE.Mesh(this._magnetGeo, this._magnetMat);
    this._magnetMesh.position.y = TABLE_Y + MAGNET_H / 2 + 0.02;
    this._inner.add(this._magnetMesh); this._meshes.push(this._magnetMesh);

    this._tableGeo = new THREE.PlaneGeometry(80, 80);
    this._tableGeo.rotateX(-Math.PI / 2);
    this._tableMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({ uBase: { value: new THREE.Color(0.010, 0.010, 0.010) } }, this._sharedUniforms),
      vertexShader: solidVertexShader, fragmentShader: tableFragmentShader
    });
    this._table = new THREE.Mesh(this._tableGeo, this._tableMat);
    this._table.position.y = TABLE_Y;
    this._inner.add(this._table); this._meshes.push(this._table);

    this._materials = [this._fluidMat, this._rimMat, this._wallMat, this._magnetMat, this._tableMat];
    this._geometries = [this._fluidGeo, this._rimGeo, this._wallGeo, this._magnetGeo, this._tableGeo];

    this._placeCamera(0);
    if (this._direct) return;

    // --- la cible MSAA privée et le quad qui l'affiche (metronome.js) ---
    const rtType = this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this._rt = new THREE.WebGLRenderTarget(4, 4, {
      samples: this._samples, type: rtType, depthBuffer: true, stencilBuffer: false,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false
    });
    this._quadMat = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: this._rt.texture } },
      vertexShader: `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }`,
      fragmentShader: `
        uniform sampler2D tScene;
        varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(tScene, vUv).rgb, 1.0); }`,
      depthTest: false, depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._quadMat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -3000;
    scene.add(this._quad);
    this._quad.onBeforeRender = function (renderer, outerScene, cam) {
      renderer.getDrawingBufferSize(_size);
      if (self._rt.width !== _size.x || self._rt.height !== _size.y) self._rt.setSize(_size.x, _size.y);
      const prevTarget = renderer.getRenderTarget();
      const prevXr = renderer.xr.enabled;
      renderer.xr.enabled = false;
      renderer.setRenderTarget(self._rt);
      renderer.clear(true, true, false);
      renderer.render(self._inner, cam);
      renderer.setRenderTarget(prevTarget);
      renderer.xr.enabled = prevXr;
    };
  }

  // --- la palette : les softboxes, saturation 0,75 ----------------------------------
  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    if (pal === this._paletteSeen) return;
    this._paletteSeen = pal;
    const v = (pal && pal.vividColors && pal.vividColors.length) ? pal.vividColors : [0xe08a3c, 0x4c8fe0, 0xd84f8c];
    const targets = [this._sharedUniforms.uBox0, this._sharedUniforms.uBox1, this._sharedUniforms.uBox2];
    for (let i = 0; i < 3; i++) {
      _color.set(v[i % v.length]);
      _color.getHSL(_hsl);
      targets[i].value.setHSL(_hsl.h, BOX_SATURATION, BOX_LIGHTNESS).multiplyScalar(BOX_RADIANCE);
    }
  }

  _random() {
    // un générateur déterministe : la même suite de positions d'aimant dans le harnais
    this._seed = (this._seed * 1664525 + 1013904223) >>> 0;
    return this._seed / 4294967296;
  }

  // L'aimant glisse vers une nouvelle position, jamais sous la pochette
  // (|m| >= MAGNET_RMIN), et jamais dans le même coin que la dernière fois.
  _newMagnetTarget() {
    const cur = Math.atan2(this._magnet.y, this._magnet.x);
    const ang = cur + Math.PI * (0.5 + this._random());
    const rad = MAGNET_RMIN + (MAGNET_RMAX - MAGNET_RMIN) * this._random();
    this._magnetFrom.copy(this._magnet);
    this._magnetTo.set(rad * Math.cos(ang), rad * Math.sin(ang));
    this._moveT = 0;
    this._moveS = MOVE_BEATS * this._secondsBeat;
  }

  _placeCamera(t) {
    const cam = this._camera;
    if (this._flat) {
      cam.up.set(0, 0, -1);
      // assez près pour que la coupelle remplisse la hauteur du cadre
      const halfH = Math.tan(THREE.MathUtils.degToRad(cam.fov || 68) / 2);
      cam.position.set(0, (R_DISH + 0.3) / halfH, 0.0001);
      cam.lookAt(0, 0, 0);
      return;
    }
    const aspect = safeZoneUniforms.uAspect.value || cam.aspect || 1.78;
    const pl = THREE.MathUtils.degToRad(PLUNGE_DEG);
    if (this._distAspect !== aspect || this._distFov !== cam.fov) {
      this._distAspect = aspect; this._distFov = cam.fov;
      this._dist = this._solveDistance(cam, aspect, pl);
    }
    const d = this._dist;
    const az = 2 * Math.PI * t / ORBIT_S;
    cam.up.set(0, 1, 0);
    cam.position.set(d * Math.cos(pl) * Math.sin(az), LOOK_Y + d * Math.sin(pl), d * Math.cos(pl) * Math.cos(az));
    // La visée : un point devant le centre, pour que la coupelle (dont le bord
    // proche paraît bien plus grand que le bord lointain) soit centrée à l'écran.
    const o = this._lookOffset;
    cam.lookAt(-o * Math.sin(az), LOOK_Y, -o * Math.cos(az));
  }

  // Projection d'un point du plan de la coupelle (x, z), caméra à (0, d sin pl,
  // d cos pl) visant (0, 0, -o) : rend (ndc.x, ndc.y) sans objet THREE.
  _project(x, z, d, pl, o, halfH, halfW) {
    const cy = d * Math.sin(pl), cz = d * Math.cos(pl);
    // axe de visée : de la caméra vers (0, 0, -o)
    let fy = -cy, fz = -o - cz;
    const fl = Math.hypot(fy, fz); fy /= fl; fz /= fl;
    // "haut" de la caméra, perpendiculaire à l'axe dans le plan vertical
    const uy = -fz, uz = fy;
    const py = 0 - cy, pz = z - cz;
    const depth = py * fy + pz * fz;
    const up = py * uy + pz * uz;
    return [x / (depth * halfW), up / (depth * halfH), depth];
  }

  // La distance à laquelle la coupelle occupe DISH_SHARE de la largeur. Le
  // bord proche est bien plus près que le centre : la largeur projetée n'est
  // pas celle du centre, on la mesure en projetant le rebord, par bissection.
  _solveDistance(cam, aspect, pl) {
    const halfH = Math.tan(THREE.MathUtils.degToRad(cam.fov || 68) / 2);
    const halfW = halfH * aspect;
    const rimR = R_DISH + 0.04 + RIM_R;
    // l'étendue projetée du rebord : [largeur, hauteur, centre vertical], en
    // fractions du cadre, caméra à d et visée décalée de o
    const extent = (d, o) => {
      let maxX = 0, minY = 9, maxY = -9;
      for (let i = 0; i < 96; i++) {
        const th = 2 * Math.PI * i / 96;
        const q = this._project(rimR * Math.cos(th), rimR * Math.sin(th), d, pl, o, halfH, halfW);
        if (q[2] <= 0.05) { maxX = 9; continue; }
        maxX = Math.max(maxX, Math.abs(q[0]));
        minY = Math.min(minY, q[1]); maxY = Math.max(maxY, q[1]);
      }
      return [maxX, (maxY - minY) / 2, (maxY + minY) / 2];
    };
    let lo = 2, hi = 60;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      const e = extent(mid, 0);
      if (e[0] > DISH_SHARE || e[1] > DISH_SHARE_H) lo = mid; else hi = mid;
    }
    const d = (lo + hi) / 2;
    // le décalage de visée qui centre la coupelle : bissection sur le centre vertical
    let olo = -3, ohi = 3;
    for (let i = 0; i < 40; i++) {
      const mid = (olo + ohi) / 2;
      if (extent(d, mid)[2] > 0) olo = mid; else ohi = mid;
    }
    this._lookOffset = (olo + ohi) / 2;
    return d;
  }

  // Les drops de l'analyse (le motif de shatter.js) : un seul par mark, re-visé sur un saut.
  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks;
      this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) {
    this._applyPalette(profile);
    this._dropIdx = 0; this._lastNowMs = null;
  }

  update(deltaTime, profile) {
    // PAUSE : gel. Le champ ne retombe pas, la caméra ne tourne pas.
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._applyPalette(profile);

    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const low = bands ? clamp01(bands.low) : 0.6;
    const mid = bands ? clamp01(bands.mid) : 0.35;
    const high = bands ? clamp01(bands.high) : 0.4;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const barS = 4 * this._secondsBeat;

    // --- la grille : temps, mesure, phrase (ou un métronome libre) ---
    let beat = false;
    const bp = profile.beatPhase;
    if (bp != null) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true;
      this._phaseBeat = bp;
    } else {
      this._free += dt;
      if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; }
    }
    let bar = false;
    const barPhase = profile.barPhase;
    if (barPhase != null) {
      if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true;
      this._phaseBar = barPhase;
    } else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) {
      if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true;
      this._phasePhrase = ph;
    } else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;

    // --- le drop : champ maximal pendant une mesure, rampe en blanc ---
    // ?ferrodrop=1 : un drop tous les 16 temps, compté sur les temps (le
    // harnais de capture recale sa position toutes les 3 s, ses phrases ne
    // bouclent jamais ; ses temps, si).
    const dropNow = this._tickDrops(profile) || (this._forceDrop && beat && this._beats % 16 === 0);
    if (dropNow) this._dropUntil = this._time + barS;
    const inDrop = this._time < this._dropUntil;

    // --- TEMPS : le champ jaillit (30 ms) et se rétracte (250 ms) ---
    if (beat) {
      this._kick.age = 0;
      this._kick.amp = H_PEAK_MIN + (H_PEAK_MAX - H_PEAK_MIN) * low;
    }
    this._kick.age += dt;
    const a = this._kick.age;
    const kickEnv = a < ATTACK_S ? a / ATTACK_S : Math.exp(-(a - ATTACK_S) / DECAY_S);
    let H = H_REST + this._kick.amp * kickEnv;
    if (inDrop) H = Math.max(H, H_DROP);
    if (this._forceH != null) H = this._forceH;
    this._H = H;

    // --- MESURE : l'enveloppe s'élargit jusqu'au bord ---
    const barT = barPhase != null ? clamp01(barPhase) : clamp01(((this._beats % 4) + this._free / this._secondsBeat) / 4);
    let sigmaTarget = SIGMA_MIN + (SIGMA_MAX - SIGMA_MIN) * Math.pow(barT, 0.7);
    if (inDrop) sigmaTarget = SIGMA_MAX;
    if (this._forceSigma != null) sigmaTarget = this._forceSigma;
    this._sigma += (sigmaTarget - this._sigma) * (1 - Math.exp(-dt / SIGMA_TAU_S));

    // --- PHRASE : l'aimant se déplace en deux temps ---
    if (phrase) this._newMagnetTarget();
    if (this._moveT < 1) {
      this._moveT = Math.min(1, this._moveT + dt / Math.max(0.2, this._moveS));
      const e = smoothstep(0, 1, this._moveT);
      this._magnet.lerpVectors(this._magnetFrom, this._magnetTo, e);
    }
    this._magnetMesh.position.x = this._magnet.x;
    this._magnetMesh.position.z = this._magnet.y;

    // --- LE PAS DU RÉSEAU : bands.mid, lissé sur une mesure ---
    const pitch = PITCH_LOW + (PITCH_HIGH - PITCH_LOW) * mid;
    this._kTarget = this._forceK != null ? this._forceK : kForPitch(pitch);
    if (this._k == null) this._k = this._kTarget;
    this._k += (this._kTarget - this._k) * (1 - Math.exp(-dt / barS));

    // --- la rampe LED : bands.high, blanc pur sur un drop ---
    const ring = inDrop ? RING_DROP : RING_RADIANCE_MIN + (RING_RADIANCE_MAX - RING_RADIANCE_MIN) * high;
    this._sharedUniforms.uRing.value.setScalar(ring);

    this._uni.uH.value = H;
    this._uni.uK.value = this._k;
    this._uni.uSigma.value = this._sigma;

    this._placeCamera(this._time);
  }

  dispose() {
    if (this._direct && this._scene) for (const m of this._meshes) this._scene.remove(m);
    if (this._scene && this._quad) { this._quad.onBeforeRender = null; this._scene.remove(this._quad); }
    if (this._quad) { this._quad.geometry.dispose(); this._quadMat.dispose(); }
    if (this._rt) this._rt.dispose();
    for (const g of this._geometries || []) g.dispose();
    for (const m of this._materials || []) m.dispose();
    if (this._env) {
      for (const f of this._env.images) f.dispose();
      this._env.dispose();
    }
    this._inner = null;
  }
}
